/**
 * The shape `GET /api/transactions/:id` answers with, and the two closed reason
 * lists its writes accept.
 *
 * WHY THESE ARE HERE AND NOT IN `@dayon/contracts`.
 *
 * Every other row type on this screen comes from that package, and this is a
 * deliberate, temporary exception: three other agents are editing this tree at
 * the same time and `packages/contracts/src/index.ts` and `lists.ts` are
 * already modified in the working copy. A fourth writer appending to the same
 * two files conflicts in the one place a bad merge is silent — a dropped enum
 * VALUE is a form that starts sending a code the route refuses. The server half
 * of the same pair lives in `services/api/src/modules/transactions/contract.ts`
 * and says the same thing; both belong in `@dayon/contracts` once the parallel
 * work has landed, and moving them is an import change with no behaviour in it.
 *
 * The two REASON LISTS below are the ones that matter: they must stay identical
 * to the server's `REVERSAL_REASONS` and `REVERSAL_REJECTIONS`, because a code
 * the select can offer and the route refuses is a form that can only ever fail.
 */

export interface LedgerEntryDetail {
  id: string;
  accountId: string;
  /** PLAYER_CASH · PLAYER_BONUS · OPERATOR_HOUSE · PSP_CLEARING · … */
  accountKind: string;
  accountPlayerId: string | null;
  accountPlayerRef: string | null;
  accountBrandName: string | null;
  /** Centavos, signed against the ACCOUNT. A string, never a number. */
  amount: string;
  currency: string;
  createdAt: string;
}

/** A transaction on the other end of a reversal link. */
export interface LinkedTransaction {
  id: string;
  ref: string;
  kind: string;
  status: string;
  /** Signed against the player's balance. Centavos, as a string. */
  amount: string;
  occurredAt: string;
}

export interface ReversalRequestDetail {
  id: string;
  ref: string;
  /** ON_THIS — a request to reverse this posting. PRODUCED_THIS — this posting IS the reversal. */
  relation: 'ON_THIS' | 'PRODUCED_THIS';
  status: 'AWAITING_CHECKER' | 'APPROVED' | 'REJECTED';
  reasonCode: string;
  reasonNote: string;
  initiatedBy: string;
  makerLabel: string | null;
  approvedBy: string | null;
  checkerLabel: string | null;
  approvedAt: string | null;
  rejectedReasonCode: string | null;
  rejectedReasonNote: string | null;
  transactionId: string;
  transactionRef: string;
  reversalTransactionId: string | null;
  reversalTransactionRef: string | null;
  createdAt: string;
}

/** Who caused a posting, resolved from the table that points at it. */
export interface TransactionActor {
  /** MANUAL_ADJUSTMENT · WITHDRAWAL · LEDGER_REVERSAL · DEPOSIT · BONUS_GRANT */
  source: string;
  sourceRef: string;
  initiatedBy: string | null;
  initiatedByLabel: string | null;
  approvedBy: string | null;
  approvedByLabel: string | null;
  decidedAt: string | null;
  /** True for a deposit: the player asked, and no operator decided anything. */
  playerInitiated: boolean;
}

export interface TransactionDetailPayload {
  id: string;
  ref: string;
  kind: string;
  status: string;
  providerErrorCode: string | null;
  externalRef: string | null;
  occurredAt: string;
  createdAt: string;
  brandId: string | null;
  brandName: string | null;
  player: { id: string; ref: string | null; name: string | null } | null;
  /** Signed against the player's balance. Centavos, as a string. */
  amount: string;
  /** The player's spendable cash right now, or null when there is no player. */
  playerCashBalance: string | null;
  entries: LedgerEntryDetail[];
  entryCount: number;
  /** Must be "0". Anything else means the zero-sum trigger was bypassed. */
  entryTotal: string;
  reverses: LinkedTransaction | null;
  reversedBy: LinkedTransaction | null;
  reversalRequests: ReversalRequestDetail[];
  openReversalRequest: ReversalRequestDetail | null;
  actors: TransactionActor[];
}

/** WHY a posting is being reversed. Must match the route's closed set exactly. */
export const REVERSAL_REASONS: readonly { code: string; label: string }[] = [
  { code: 'DUPLICATE_POSTING', label: 'Duplicate posting — the same movement was recorded twice' },
  { code: 'WRONG_PLAYER', label: 'Wrong player — posted against the wrong account' },
  { code: 'WRONG_AMOUNT', label: 'Wrong amount — the figure posted is not the figure instructed' },
  { code: 'PROVIDER_CHARGEBACK', label: 'Provider chargeback — the rail took the money back' },
  { code: 'OPERATOR_ERROR', label: 'Operator error — a manual posting made in error' },
  { code: 'REGULATORY_ORDER', label: 'Regulatory or compliance instruction' },
];

/** Why a checker REFUSED one. A different question, so a different list. */
export const REVERSAL_REJECTIONS: readonly { code: string; label: string }[] = [
  { code: 'POSTING_IS_CORRECT', label: 'The posting is correct as it stands' },
  { code: 'WRONG_REMEDY', label: 'A reversal is the wrong remedy — raise an adjustment instead' },
  { code: 'INSUFFICIENT_EVIDENCE', label: 'Not enough evidence to reverse settled money' },
  { code: 'DUPLICATE_REQUEST', label: 'Already being handled elsewhere' },
];

/** What an account is called on screen. The database stores the enum. */
export const ACCOUNT_KIND_LABEL: Record<string, string> = {
  PLAYER_CASH: 'Player cash',
  PLAYER_BONUS: 'Player bonus',
  PLAYER_LOCKED: 'Player locked',
  OPERATOR_HOUSE: 'House',
  PROVIDER_CLEARING: 'Game provider clearing',
  PSP_CLEARING: 'Payment provider clearing',
  PROMO_FUNDING: 'Promotion budget',
  COMMISSION_PAYABLE: 'Commission payable',
};

/** What produced a posting, in the words an operator uses. */
export const ACTOR_SOURCE_LABEL: Record<string, string> = {
  MANUAL_ADJUSTMENT: 'Manual balance adjustment',
  WITHDRAWAL: 'Withdrawal payout',
  LEDGER_REVERSAL: 'Ledger reversal',
  DEPOSIT: 'Player deposit',
  BONUS_GRANT: 'Bonus grant',
};

/**
 * WHY THIS POSTING CANNOT BE REVERSED — or null when it can.
 *
 * The same tests the route makes, in the same ORDER, so the sentence on the
 * screen is the sentence the API would have returned. The route is the
 * authority: this exists so the control can be disabled with its true reason
 * showing rather than offered and then refused.
 */
export function whyNotReversible(d: TransactionDetailPayload): string | null {
  if (d.kind === 'REVERSAL') {
    return (
      `${d.ref} is itself a reversal, and the mirror of a mirror is the original posting. ` +
      'A correction to a correction is raised as a manual balance adjustment, which records ' +
      'what happened in its own note rather than adding a link to a chain.'
    );
  }
  if (d.reverses) {
    return (
      `${d.ref} already reverses ${d.reverses.ref}, so it is a correction rather than a ` +
      'movement to correct. Raise a manual balance adjustment instead.'
    );
  }
  if (d.status === 'PENDING') {
    return `${d.ref} is still pending, so nothing has settled to reverse.`;
  }
  if (d.entryCount === 0) {
    return (
      `${d.ref} has no ledger entries, so there is nothing to mirror. A failed movement records ` +
      'the attempt and moves no money.'
    );
  }
  if (d.reversedBy) {
    return `${d.ref} has already been reversed by ${d.reversedBy.ref}. A posting is reversed once.`;
  }
  return null;
}
